import { parsePhoneNumber, isValidPhoneNumber, CountryCode } from 'libphonenumber-js';

/**
 * Normaliza un número telefónico a formato internacional estándar
 * @param phoneNumber Número telefónico en cualquier formato
 * @param country Código de país por defecto (ej: 'MX', 'US')
 * @returns Número normalizado en formato E.164 (+5255123456) o null si inválido
 */
export function normalizePhoneNumber(phoneNumber: string, country: CountryCode = 'MX'): string | null {
  if (!phoneNumber?.trim()) {
    return null;
  }

  try {
    // Limpiar el número (remover espacios, guiones, paréntesis)
    const cleanNumber = phoneNumber.replace(/[\s\-\(\)\.]/g, '');
    
    // Intentar parsear con el país por defecto
    const parsed = parsePhoneNumber(cleanNumber, country);
    
    if (parsed && parsed.isValid()) {
      // Retornar en formato E.164 (ej: +5255123456)
      return parsed.format('E.164');
    }

    // Si falla, intentar como número internacional
    const parsedIntl = parsePhoneNumber(cleanNumber);
    if (parsedIntl && parsedIntl.isValid()) {
      return parsedIntl.format('E.164');
    }

    return null;
  } catch (error) {
    console.warn('Error normalizing phone number:', phoneNumber, error);
    return null;
  }
}

/**
 * Valida si un número telefónico es válido
 * @param phoneNumber Número telefónico
 * @param country Código de país por defecto
 * @returns true si es válido
 */
export function validatePhoneNumber(phoneNumber: string, country: CountryCode = 'MX'): boolean {
  if (!phoneNumber?.trim()) {
    return false;
  }

  try {
    return isValidPhoneNumber(phoneNumber, country) || isValidPhoneNumber(phoneNumber);
  } catch (error) {
    return false;
  }
}

/**
 * Formatea un número telefónico para mostrar al usuario
 * @param phoneNumber Número en formato E.164
 * @returns Número formateado para lectura (ej: +52 55 1234 5678)
 */
export function formatPhoneForDisplay(phoneNumber: string): string {
  if (!phoneNumber) return '';

  try {
    const parsed = parsePhoneNumber(phoneNumber);
    if (parsed && parsed.isValid()) {
      return parsed.formatInternational();
    }
    return phoneNumber;
  } catch (error) {
    return phoneNumber;
  }
}

/**
 * Extrae información del país de un número telefónico
 * @param phoneNumber Número telefónico
 * @returns Información del país
 */
export function getPhoneCountryInfo(phoneNumber: string) {
  if (!phoneNumber?.trim()) {
    return null;
  }

  try {
    const parsed = parsePhoneNumber(phoneNumber);
    if (parsed && parsed.isValid()) {
      return {
        country: parsed.country,
        countryCallingCode: parsed.countryCallingCode,
        nationalNumber: parsed.nationalNumber
      };
    }
    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Lista de países más comunes para el selector
 */
export const COMMON_COUNTRIES: CountryCode[] = [
  'MX', // México
  'US', // Estados Unidos  
  'CA', // Canadá
  'ES', // España
  'CO', // Colombia
  'AR', // Argentina
  'CL', // Chile
  'PE', // Perú
  'VE', // Venezuela
  'EC', // Ecuador
  'GT', // Guatemala
  'CR', // Costa Rica
  'PA', // Panamá
  'NI', // Nicaragua
  'HN', // Honduras
  'SV', // El Salvador
  'BZ', // Belice
];

/**
 * Configuración por defecto para México
 */
export const DEFAULT_COUNTRY: CountryCode = 'MX';

/**
 * Máscara los dígitos centrales de un teléfono para privacidad
 * @param phoneNumber Número telefónico
 * @returns Número enmascarado (ej: +52 55 ***4567)
 */
export function maskPhoneNumber(phoneNumber: string): string {
  if (!phoneNumber) return '';

  try {
    const parsed = parsePhoneNumber(phoneNumber);
    if (parsed && parsed.isValid()) {
      const formatted = parsed.formatInternational();
      const parts = formatted.split(' ');
      
      if (parts.length >= 3) {
        // Enmascarar los dígitos del medio
        const lastPart = parts[parts.length - 1];
        if (lastPart.length > 4) {
          const visible = 2;
          const masked = '*'.repeat(lastPart.length - visible);
          parts[parts.length - 1] = masked + lastPart.slice(-visible);
        }
        return parts.join(' ');
      }
    }
    return phoneNumber;
  } catch (error) {
    return phoneNumber;
  }
}

/**
 * Compara dos números telefónicos normalizados
 * @param phone1 Primer número
 * @param phone2 Segundo número  
 * @returns true si son el mismo número
 */
export function comparePhoneNumbers(phone1: string, phone2: string, country: CountryCode = 'MX'): boolean {
  const normalized1 = normalizePhoneNumber(phone1, country);
  const normalized2 = normalizePhoneNumber(phone2, country);
  
  return normalized1 !== null && normalized2 !== null && normalized1 === normalized2;
}